/**
 * Russian message catalog — mirrors the shape of `messages.en.ts`
 * exactly; `satisfies MessagesShape` makes a missing or renamed key a
 * TypeScript error. Register: вы, lowercase (locked in l10n/locales.md).
 *
 * Platform terms follow Russian UI conventions (настройки, сопряжение
 * for pairing, дублирование for notification mirroring, удалить for
 * dismiss, беседа for conversation). "Glance" is the untranslated
 * feature brand name (glossary D1); «клип» renders the D2 loanword in
 * Cyrillic (locked CJK/Cyrillic clarification).
 *
 * Plural notes: Russian is a {one, few, many, other} CLDR locale —
 * every count-bearing `selectPlural("ru", …)` supplies all four forms.
 * `one` covers 21, 31, 41… (not just 1), so numbers are never hardcoded
 * in the `one` form.
 *
 * Out of scope: the same exclusions as the English catalog (log
 * scopes, structured event names, JSDoc, server-emitted error
 * messages — see `messages.en.ts` for the full list).
 */
import { selectPlural } from "@reflecto/client-core";
import type { MessagesShape } from ".";

export const messages = {
  header: {
    banner: "Reflecto",
    statusConnected: "Подключено",
    statusReconnecting: "Переподключение…",
    statusDisconnected: "Отключено",
    openSettings: "Открыть настройки",
    pairedFallbackDevice: "ваш телефон",
    publicModeToggleEnable: "Скрыть содержимое уведомлений",
    publicModeToggleDisable: "Показать содержимое уведомлений",
  },

  notifications: {
    replyAriaLabel: "Ответить",
    replyPlaceholder: "Ответить…",
    replySend: "Отправить",
    sent: "Отправлено",
    failedToSend: "Не удалось отправить",
    unread: "Непрочитано",
    dismiss: "Удалить",
    notificationImageAlt: "Изображение из уведомления",
  },

  conversations: {
    replyAriaLabel: "Ответить",
    replyPlaceholder: "Ответить…",
    replySend: "Отправить",
    sent: "Отправлено",
    failedToSend: "Не удалось отправить",
    dismiss: "Удалить беседу",
    messagesListLabel: "Сообщения в этой беседе",
    deviceOwner: "Вы",
    unreadAriaLabel: (count: number): string =>
      count === 0
        ? "Нет непрочитанных сообщений"
        : `${count} ${selectPlural("ru", count, {
            one: "непрочитанное сообщение",
            few: "непрочитанных сообщения",
            many: "непрочитанных сообщений",
            other: "непрочитанного сообщения",
          })}`,
  },

  list: {
    today: "Сегодня",
    yesterday: "Вчера",
    searchNotifications: "Поиск уведомлений",
    clearSearch: "Очистить поиск",
    clearAll: "Удалить все",
    markAllRead: "Отметить все как прочитанные",
    groupCheckboxLabel: "Группировать",
    noNotificationsTitle: "Уведомлений пока нет",
    noNotificationsDesc: "Новые уведомления, продублированные с телефона, появятся здесь.",
    noMatchesTitle: "Ничего не найдено",
    notificationsList: "Уведомления",
    privacyAppList: "Уведомления по приложениям",
    clear: "Удалить",
    clearMatches: (count: number): string =>
      `Удалить ${count} ${selectPlural("ru", count, {
        one: "совпадение",
        few: "совпадения",
        many: "совпадений",
        other: "совпадения",
      })}`,
    noMatchesDesc: (query: string): string => `Нет уведомлений по запросу «${query}».`,
    appNotificationsGroup: (appName: string): string => `Уведомления ${appName}`,
    dismissAllFrom: (appName: string): string => `Удалить все от ${appName}`,
    privacyCount: (count: number, kind: "messages" | "calls" | "notifications") => {
      const noun =
        kind === "messages"
          ? selectPlural("ru", count, {
              one: "сообщение",
              few: "сообщения",
              many: "сообщений",
              other: "сообщения",
            })
          : kind === "calls"
            ? selectPlural("ru", count, {
                one: "звонок",
                few: "звонка",
                many: "звонков",
                other: "звонка",
              })
            : selectPlural("ru", count, {
                one: "уведомление",
                few: "уведомления",
                many: "уведомлений",
                other: "уведомления",
              });
      return `${count} ${noun}`;
    },
    kbdDismiss: "удалить",
    kbdNavigate: "навигация",
    kbdOrSeparator: "или",
  },

  settings: {
    back: "Назад к уведомлениям",
    settings: "Настройки",
    appearance: "Внешний вид",
    theme: "Тема",
    themeAuto: "Автоматическая",
    themeLight: "Светлая",
    themeDark: "Тёмная",
    language: "Язык",
    languageSystemDefault: "Как в системе",
    notificationsSection: "Уведомления",
    osNotifications: "Системные уведомления",
    callNotifications: "Уведомления о звонках",
    privacySection: "Конфиденциальность",
    analyticsToggle: "Анонимная статистика использования",
    analyticsToggleDescription: "Только счётчики и вехи — никогда ваши уведомления.",
    connection: "Подключение",
    status: "Статус",
    statusConnected: "Подключено",
    statusReconnecting: "Переподключение…",
    statusDisconnected: "Отключено",
    disconnectFromPhone: "Отключить от телефона",
    disconnectConfirm: "Вы уверены? Сопряжение с устройством будет разорвано.",
    yesDisconnect: "Да, отключить",
    cancel: "Отмена",
    aboutAndSupport: "О приложении и поддержка",
    version: "Версия",
    encryption: "Шифрование",
    encryptionValue: "E2E (NaCl)",
    needHelp: "Нужна помощь? Напишите на __EMAIL__",
    disconnectFailed: "Не удалось отключить — попробуйте ещё раз",
    disconnectLocalCleanupFailedWithError: (errorMsg: string): string =>
      `Отключено, но не удалось очистить локальные данные: ${errorMsg}. Перезагрузите страницу.`,
    disconnectLocalCleanupFailed:
      "Отключено, но не удалось очистить локальные данные. Перезагрузите страницу.",
  },

  pairing: {
    enterCodeInstruction: "Введите этот код в приложении Reflecto",
    pairedSuccessfully: "Сопряжение выполнено!",
    pairingFailed: "Сопряжение не удалось",
    requestingPairingCode: "Запрашиваем код сопряжения…",
    codeExpired: "Срок действия кода истёк",
    tryAgain: "Попробовать ещё раз",
    failedToStartPairing: "Не удалось начать сопряжение",
    needAppText: "Нужно приложение для Android? Напишите на __EMAIL__, чтобы получить доступ.",
  },

  dnd: {
    title: "Не беспокоить",
    description: "Приостановить уведомления на этом устройстве на ближайший час.",
    snooze1h: "Отложить на 1 час",
    snoozeActiveUntil: (formattedTime: string): string => `Активно до ${formattedTime}`,
    cancelSnooze: "Отмена",
  },

  connectors: {
    title: "Подключённые сервисы",
    emptyBody:
      "Подключённых сервисов пока нет. Подключите ChatGPT или Claude в настройках их коннекторов, затем подтвердите код на телефоне.",
    scopeSend: "Может отправлять уведомления",
    added: (date: string): string => `Добавлен ${date}`,
    lastUsed: (relative: string): string => `Последнее использование ${relative}`,
    neverUsed: "Не использовался",
    revoke: "Отключить",
    revokeConfirm: (label: string): string =>
      `Отключить ${label}? Доступ будет потерян немедленно.`,
    revokeAction: "Отключить",
    revokeConfirmLabel: (label: string): string => `Отключить ${label}`,
    cancel: "Отмена",
    loadError: "Не удалось загрузить подключённые сервисы. Попробуйте ещё раз.",
    revokeError: "Не удалось отключить сервис. Попробуйте ещё раз.",
    retry: "Повторить",
  },

  feedback: {
    title: "Отправить отзыв",
    close: "Закрыть",
    messageLabel: "Ваше сообщение",
    messagePlaceholder: "Нашли баг, есть идея или что-то не так? Расскажите нам.",
    categoryLabel: "Категория",
    categoryBug: "Баг",
    categoryIdea: "Идея",
    categoryOther: "Другое",
    emailLabel: "Эл. почта (необязательно, только если хотите получить ответ)",
    emailPlaceholder: "you@example.com",
    emailHelp: "Мы не добавим вас ни в какие рассылки.",
    disclosure:
      "Ваше сообщение отправляется на сервер Reflecto и сохраняется в нашем внутреннем трекере задач. Мы прикладываем версию приложения и идентификатор устройства, присвоенный при сопряжении, чтобы сопоставлять отчёты. Мы не видим ваши уведомления, контакты и другие личные данные. Эл. почта необязательна — укажите её, только если хотите получить ответ. Мы не добавим вас ни в какие рассылки.",
    submit: "Отправить отзыв",
    sending: "Отправляем…",
    success: "Спасибо — отзыв прочитает человек, обычно в течение 24 часов",
    error: "Не удалось отправить. Попробуйте ещё раз.",
    done: "Готово",
    sendFeedback: "Отправить отзыв",
  },

  glance: {
    // Brand term (glossary D1): the feature name "Glance" is the heading
    // itself in non-English locales — the EN "At a glance" wordplay is
    // EN-only.
    title: "Glance",
    appName: "Reflecto",
    closeLabel: "Закрыть",
    aiSummaryTag: "Сводка ИИ",
    errorAi: "Сейчас не удалось составить сводку.",
    retryCta: "Попробовать ещё раз",
    downloadHeading: "Сводки на базе ИИ",
    downloadBody:
      "Догоняйте пропущенное со сводкой от ИИ прямо на устройстве. Всё работает только на вашем устройстве.",
    downloadCta: "Скачать модель",
    downloadPrivacyNote: "Одноразовая загрузка. Сервер никогда не видит ваши уведомления.",
    downloadingHeading: "Скачиваем модель…",
    caughtUp: "Вы всё просмотрели — сейчас ничего не требует вашего внимания.",
    needsYou: "Требует внимания",
    quiet: "Остальное",
    scanning: (pending: number): string => `Готовим сводку: ${pending}…`,
    jump: "Открыть уведомление",
    affordanceReply: "Ответить",
    affordanceOpen: "Открыть",
    affordanceCopyOtp: "Копировать код",
    buttonLabel: "Glance",
  },

  update: {
    title: "Доступно обновление",
    description: "Готова новая версия Reflecto. Перезагрузите страницу, чтобы обновиться.",
    reload: "Перезагрузить",
  },

  pairConfirm: {
    cardMessage: (name: string): string => `Мы назвали это устройство «${name}».`,
    cardLooksGood: "Подходит",
    cardEdit: "Изменить",
    cardAriaLabel: "Подтвердите имя устройства",
    renameTitle: "Переименовать это устройство",
    renameLabel: "Имя устройства",
    renameSave: "Сохранить",
    renameCancel: "Отмена",
    renameFailed: "Не удалось переименовать. Попробуйте ещё раз.",
  },

  collision: {
    title: (name: string): string => `У вас уже есть «${name}»`,
    subtitle: "Как назвать это устройство?",
    chipWork: (name: string): string => `Рабочий ${name}`,
    chipHome: (name: string): string => `Домашний ${name}`,
    chipPersonal: (name: string): string => `Личный ${name}`,
    customLabel: "Своё имя",
    save: "Сохранить",
    useAnyway: "Всё равно использовать исходное имя",
    saveFailed: "Не удалось сохранить новое имя. Попробуйте ещё раз.",
    ariaLabel: "Устраните совпадение имён устройств",
  },

  clipboard: {
    title: "Буфер обмена",
    empty: "Клипы с других ваших устройств появляются здесь. Нажмите, чтобы скопировать.",
    from: (label: string): string => `от ${label}`,
    copy: "Копировать в буфер обмена",
    copied: "Скопировано",
    reveal: "Показать",
    hide: "Скрыть",
    sensitiveChip: "Конфиденциально",
    cloudChip: "через ваш токен API",
    maskedHint: "Содержимое скрыто — нажмите «Показать»",
    sendPlaceholder: "Вставьте текст для отправки…",
    sendCta: "Отправить",
    sendSensitiveToggle: "Отметить как конфиденциальный",
    sendSuccess: (n: number): string =>
      `Отправлено на ${n} ${selectPlural("ru", n, {
        one: "устройство",
        few: "устройства",
        many: "устройств",
        other: "устройства",
      })}.`,
    sendPartial: (n: number, total: number): string =>
      `Отправлено на ${n} из ${total} ${selectPlural("ru", total, {
        one: "устройства",
        few: "устройств",
        many: "устройств",
        other: "устройства",
      })}.`,
    sendNoDevices: "Нет сопряжённых устройств для отправки.",
    sendFailed: "Не удалось отправить. Попробуйте ещё раз.",
    sendEmpty: "Нечего отправлять.",
    dockExpand: "Показать историю клипов",
    dockCollapse: "Свернуть",
    dockSendTitle: "Отправить на ваши устройства",
    dockFromClipboard: (chars: number): string =>
      `Из буфера обмена · ${chars} ${selectPlural("ru", chars, {
        one: "символ",
        few: "символа",
        many: "символов",
        other: "символа",
      })}`,
    dockSend: (n: number): string => `Отправить на ${n}`,
    dockCancel: "Отмена",
    dockRecentHeading: "Недавние",
    dockEmptyClipboard: "Буфер обмена пуст.",
    shareTitle: "Отправить на ваши устройства",
    shareReview:
      "Проверьте, что вы отправляете. Данные уйдут на ваши сопряжённые устройства со сквозным шифрованием.",
    shareSend: "Отправить",
    shareCancel: "Отмена",
    shareNotPaired: "Чтобы отправить общий текст, сначала выполните сопряжение с устройством.",
    shareOpenApp: "Открыть Reflecto",
  },
  install: {
    title: "Установить Reflecto",
    body: "Добавьте на главный экран для быстрого доступа.",
    cta: "Установить",
    dismissAria: "Закрыть предложение об установке",
  },
  common: {
    loading: "Загрузка",
  },
  errors: {
    notPaired: "Не сопряжено",
  },
  device: {
    nameTemplate: (browser: string, os: string) => `${browser} на ${os}`,
    webFallback: "Веб-браузер",
  },
  palette: {
    placeholder: "Введите команду…",
    noResults: "Команды не найдены",
    ariaLabel: "Палитра команд",
    clearAll: "Удалить все уведомления",
    glance: "Сводка Glance",
    cancelSnooze: "Отменить отсрочку",
    copyClip: "Копировать последний полученный клип",
  },
} satisfies MessagesShape;
